import { IProduct, IRateResSchema } from '@monetize/types/app';
import { useEffect, useMemo, useRef, useState } from 'react';
import { checkIfProductsHaveBuckets } from '../../../../../../utils/rates';

interface UseRateFormStateParams {
  id: string;
  products: IProduct[];
  offeringRate: IRateResSchema | null;
  isClonedRate: boolean;
  isExpandedAll: boolean;
  name?: string;
  isFetching: boolean;
}

/**
 * Custom hook to manage rate form state and derived values
 */
export const useRateFormState = ({
  id,
  products,
  offeringRate,
  isClonedRate,
  isExpandedAll,
  name,
  isFetching,
}: UseRateFormStateParams) => {
  const nameRef = useRef<HTMLInputElement | null>(null);
  // v3's Accordion.Root tracks expanded items by string `value`, not a
  // numeric index - this form has a single item at value "0", so
  // expandedValue is `['0']` when open and `[]` when closed.
  const [expandedValue, setExpandedValue] = useState<string[]>([]);

  // Computed values
  const isNewRate = id?.match('new-') !== null;
  const isLocked = Boolean(offeringRate?.locked);

  const isCreditAllocationFrequencyRequired = useMemo(() => {
    return checkIfProductsHaveBuckets(products);
  }, [products]);

  // Auto-expand accordion for new or cloned rates
  useEffect(() => {
    if (isClonedRate || isNewRate) {
      const timeoutId = setTimeout(() => {
        setExpandedValue(['0']);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isClonedRate, isNewRate]);

  // Handle expand all toggle
  useEffect(() => {
    setExpandedValue(isExpandedAll ? ['0'] : []);
  }, [isExpandedAll]);

  // Focus name input when accordion expands
  useEffect(() => {
    if (expandedValue.includes('0') && !isExpandedAll) {
      nameRef.current?.focus();
    }
  }, [expandedValue, isExpandedAll]);

  // Determine if skeleton should be shown
  // Show skeleton when fetching OR when it's an existing rate without data yet
  const isExistingRateLoading =
    !isNewRate && !isClonedRate && name === undefined;
  const shouldShowSkeleton = isFetching || isExistingRateLoading;

  return {
    nameRef,
    expandedValue,
    setExpandedValue,
    isNewRate,
    isLocked,
    isCreditAllocationFrequencyRequired,
    shouldShowSkeleton,
  };
};
